import React from 'react';
import { Card } from 'antd';
import { useLocation } from 'react-router-dom';
import { SkBar } from './_primitives';

const TAB_ORDER = ['moods', 'gatherings', 'banners', 'occasions', 'stories'];

const ContentHeader = () => (
  <div className="flex flex-col gap-2 mb-4">
    <SkBar h="h-7" w="w-64" className="max-w-full" />
    <SkBar h="h-3" w="w-96" className="max-w-full" />
  </div>
);

const TabsRow = ({ active = 0 }) => (
  <div className="flex gap-4 sm:gap-6 border-b border-gray-200 mb-4 overflow-x-auto">
    {TAB_ORDER.map((_, i) => (
      <div
        key={i}
        className={`pb-2 flex-shrink-0 ${active === i ? 'border-b-2 border-gray-300' : ''}`}
      >
        <SkBar h="h-4" w="w-28" />
      </div>
    ))}
  </div>
);

/* ─────────── Default list tab (Moods / Gatherings / Banners / Stories) ─────────── */

const DefaultActionRow = () => (
  <div className="flex items-center justify-between gap-3 mb-4">
    <SkBar h="h-9" w="w-32" className="rounded-md" />
    <SkBar h="h-9" w="w-28" className="rounded-md" />
  </div>
);

const DEFAULT_COLS = ['w-14', 'w-14', 'w-24', 'w-14', 'w-14', 'w-16'];

const DefaultRow = () => (
  <div className="grid grid-cols-[0.8fr_2fr_2fr_1fr_1fr_1.2fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    <div className="w-12 h-12 rounded-lg bg-gray-200 animate-pulse" />
    <SkBar h="h-3" w="w-24" />
    <SkBar h="h-3" w="w-16" />
    <SkBar h="h-6" w="w-16" className="rounded-md" />
    <SkBar h="h-6" w="w-16" className="rounded-full" />
    <div className="flex items-center gap-2 justify-end">
      <SkBar h="h-8" w="w-8" className="rounded-md" />
      <SkBar h="h-8" w="w-8" className="rounded-md" />
      <SkBar h="h-8" w="w-8" className="rounded-md" />
    </div>
  </div>
);

const DefaultTabContent = () => (
  <>
    <DefaultActionRow />
    <div className="overflow-x-auto">
      <div className="min-w-[900px]">
        <div className="grid grid-cols-[0.8fr_2fr_2fr_1fr_1fr_1.2fr] gap-3 pb-3 border-b border-gray-200 bg-gray-50 px-2 py-2 rounded-t">
          {DEFAULT_COLS.map((w, i) => (
            <SkBar
              key={i}
              h="h-3"
              w={w}
              className={i === DEFAULT_COLS.length - 1 ? 'justify-self-end' : ''}
            />
          ))}
        </div>
        {Array.from({ length: 6 }).map((_, i) => (
          <DefaultRow key={i} />
        ))}
      </div>
    </div>
  </>
);

/* ─────────── Occasions (Wrth) tab ─────────── */

const OccasionsActionRow = () => (
  <div className="flex items-center justify-between gap-3 mb-4">
    <div className="flex items-center gap-2">
      <SkBar h="h-9" w="w-36" className="rounded-md" />
      <SkBar h="h-9" w="w-28" className="rounded-md" />
    </div>
    <div className="flex items-center gap-2">
      <SkBar h="h-3" w="w-16" />
      <SkBar h="h-6" w="w-12" className="rounded-full" />
    </div>
  </div>
);

const OCCASION_COLS = ['w-8', 'w-28', 'w-12', 'w-14', 'w-20', 'w-16'];

const OccasionRow = () => (
  <div className="grid grid-cols-[0.5fr_2.5fr_1fr_1fr_1.5fr_1fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* ID */}
    <SkBar h="h-3" w="w-4" />
    {/* Location Name */}
    <SkBar h="h-3" w="w-56" />
    {/* Items pill */}
    <SkBar h="h-6" w="w-16" className="rounded-md justify-self-center" />
    {/* Status pill */}
    <SkBar h="h-6" w="w-16" className="rounded-md justify-self-center" />
    {/* Created At */}
    <SkBar h="h-3" w="w-36" />
    {/* Actions: eye + delete */}
    <div className="flex items-center gap-3 justify-end">
      <SkBar h="h-6" w="w-6" className="rounded-md" />
      <SkBar h="h-6" w="w-6" className="rounded-md" />
    </div>
  </div>
);

const OccasionsPagination = () => (
  <div className="flex items-center justify-end gap-3 mt-4 pt-3">
    <SkBar h="h-3" w="w-28" />
    <div className="flex items-center gap-2">
      <SkBar h="h-8" w="w-8" className="rounded-md" />
      <SkBar h="h-8" w="w-8" className="rounded-md" />
      <SkBar h="h-8" w="w-8" className="rounded-md" />
    </div>
    <SkBar h="h-8" w="w-24" className="rounded-md" />
  </div>
);

const OccasionsTabContent = () => (
  <>
    <OccasionsActionRow />
    <div className="overflow-x-auto">
      <div className="min-w-[900px]">
        <div className="grid grid-cols-[0.5fr_2.5fr_1fr_1fr_1.5fr_1fr] gap-3 pb-3 border-b border-gray-200 bg-gray-50 px-2 py-2 rounded-t">
          {OCCASION_COLS.map((w, i) => {
            const isLast = i === OCCASION_COLS.length - 1;
            const isCenter = [2, 3].includes(i);
            return (
              <SkBar
                key={i}
                h="h-3"
                w={w}
                className={
                  isLast ? 'justify-self-end' : isCenter ? 'justify-self-center' : ''
                }
              />
            );
          })}
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <OccasionRow key={i} />
        ))}
      </div>
    </div>
    <OccasionsPagination />
  </>
);

/* ─────────── Stories (Story Config) tab ─────────── */

const StoriesSubTabs = () => (
  <div className="flex gap-6 border-b border-gray-200 mb-4 overflow-x-auto">
    <div className="pb-2 flex-shrink-0">
      <SkBar h="h-4" w="w-32" />
    </div>
    <div className="pb-2 flex-shrink-0 border-b-2 border-gray-300">
      <SkBar h="h-4" w="w-28" />
    </div>
  </div>
);

const StoriesActionRow = () => (
  <div className="flex items-center justify-between gap-3 mb-4">
    <SkBar h="h-9" w="w-36" className="rounded-md" />
    <SkBar h="h-9" w="w-28" className="rounded-md" />
  </div>
);

const STORIES_COLS = ['w-8', 'w-16', 'w-14', 'w-16'];

const StoryRow = () => (
  <div className="grid grid-cols-[0.5fr_3fr_1fr_1fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* ID */}
    <SkBar h="h-3" w="w-4" />
    {/* Name */}
    <SkBar h="h-3" w="w-28" />
    {/* Status pill */}
    <SkBar h="h-6" w="w-16" className="rounded-md justify-self-center" />
    {/* Actions: edit + delete */}
    <div className="flex items-center gap-3 justify-end">
      <SkBar h="h-6" w="w-6" className="rounded-md" />
      <SkBar h="h-6" w="w-6" className="rounded-md" />
    </div>
  </div>
);

const StoriesTabContent = () => (
  <>
    <StoriesSubTabs />
    <StoriesActionRow />
    <div className="overflow-x-auto">
      <div className="min-w-[700px]">
        <div className="grid grid-cols-[0.5fr_3fr_1fr_1fr] gap-3 pb-3 border-b border-gray-200 bg-gray-50 px-2 py-2 rounded-t">
          {STORIES_COLS.map((w, i) => {
            const isLast = i === STORIES_COLS.length - 1;
            const isStatus = i === 2;
            return (
              <SkBar
                key={i}
                h="h-3"
                w={w}
                className={
                  isLast ? 'justify-self-end' : isStatus ? 'justify-self-center' : ''
                }
              />
            );
          })}
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <StoryRow key={i} />
        ))}
      </div>
    </div>
  </>
);

/* ─────────── Main loader ─────────── */

const ContentManagementPageLoader = () => {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get('tab') || 'moods';
  const activeIndex = Math.max(0, TAB_ORDER.indexOf(tab));

  let content;
  if (tab === 'occasions') content = <OccasionsTabContent />;
  else if (tab === 'stories') content = <StoriesTabContent />;
  else content = <DefaultTabContent />;

  return (
    <div className="w-full p-3 sm:p-4 md:p-6">
      <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
        <ContentHeader />
        <TabsRow active={activeIndex} />
        {content}
      </Card>
    </div>
  );
};

export default ContentManagementPageLoader;
